import swal from 'sweetalert';

/**
 * Muestra una alerta de éxito con SweetAlert
 * @param {string} title - Título de la alerta
 * @param {string} text - Texto descriptivo
 */
export const showSuccess = (title, text) => {
  swal({
    title,
    text,
    icon: "success",
    buttons: {
      confirm: {
        text: "Aceptar",
        value: true,
        visible: true,
        className: "bg-blue-500 hover:bg-blue-600",
        closeModal: true
      }
    }
  });
};

/**
 * Muestra una alerta de error con SweetAlert
 * @param {string} title - Título de la alerta
 * @param {string} text - Texto descriptivo
 * @param {string} type - Tipo de notificación: "error", "success", "warning", "info"
 */
export const showError = (title, text, type = "error") => {
  const configs = {
    error: {
      icon: "error",
      className: "bg-red-500 hover:bg-red-600"
    },
    success: {
      icon: "success", 
      className: "bg-green-500 hover:bg-green-600"
    },
    warning: {
      icon: "warning",
      className: "bg-yellow-500 hover:bg-yellow-600"
    },
    info: {
      icon: "info",
      className: "bg-blue-500 hover:bg-blue-600"
    }
  };

  const config = configs[type] || configs.error;

  swal({
    title,
    text,
    icon: config.icon,
    buttons: {
      confirm: {
        text: "Aceptar",
        value: true,
        visible: true,
        className: config.className,
        closeModal: true
      }
    }
  });
};

/**
 * Muestra una alerta de advertencia con SweetAlert
 * @param {string} title - Título de la alerta
 * @param {string} text - Texto descriptivo
 */
export const showWarning = (title, text) => {
  swal({
    title,
    text,
    icon: "warning",
    buttons: {
      confirm: {
        text: "Aceptar",
        value: true,
        visible: true,
        className: "bg-yellow-500 hover:bg-yellow-600",
        closeModal: true
      }
    }
  });
};

/**
 * Muestra un diálogo de confirmación con SweetAlert
 * @param {string} title - Título de la alerta
 * @param {string} text - Texto descriptivo
 * @param {string} confirmText - Texto del botón de confirmación
 * @returns {Promise<boolean>} - Promise que se resuelve cuando el usuario hace click
 */
export const showConfirm = async (title, text, confirmText = "Confirmar") => {
  const result = await swal({
    title,
    text,
    icon: "info",
    buttons: {
      cancel: {
        text: "Cancelar",
        value: false,
        visible: true,
        className: "bg-gray-300 hover:bg-gray-400",
        closeModal: true,
      },
      confirm: {
        text: confirmText,
        value: true,
        visible: true,
        className: "bg-blue-500 hover:bg-blue-600",
        closeModal: true
      }
    }
  });

  return result;
};

/**
 * Muestra una notificación de recarga exitosa
 * @param {string} vendorName - Nombre del vendedor
 * @param {number} amount - Monto de la recarga
 * @param {number} newBalance - Nuevo saldo después de la recarga
 */
export const showRechargeSuccess = (vendorName, amount, newBalance) => {
  swal({
    title: "¡Recarga Exitosa!",
    text: `Se ha recargado $${amount} al vendedor ${vendorName}. Nuevo saldo: $${newBalance}`,
    icon: "success",
    button: "Aceptar"
  });
};

/**
 * Muestra un diálogo de confirmación de recarga
 * @param {string} vendorName - Nombre del vendedor
 * @param {number} amount - Monto de la recarga
 * @returns {Promise<boolean>} - Promise que se resuelve cuando el usuario hace click
 */
export const confirmRecharge = async (vendorName, amount) => {
  const result = await swal({
    title: "Confirmar Recarga",
    text: `¿Estás seguro de recargar $${amount} al vendedor ${vendorName}?`,
    icon: "warning",
    buttons: {
      cancel: "Cancelar",
      confirm: {
        text: "Sí, Recargar",
        value: true,
      }
    },
    dangerMode: true,
  });

  return result;
};

/**
 * Muestra el resultado detallado de una recarga
 * @param {string} vendorName - Nombre del vendedor
 * @param {number} amount - Monto de la recarga
 * @param {number} newBalance - Nuevo saldo después de la recarga
 */
export const showRechargeResult = (vendorName, amount, newBalance) => {
  swal({
    title: "Recarga Completada",
    text: `
      La recarga se ha procesado correctamente:
      • Vendedor: ${vendorName}
      • Monto: $${amount}
      • Nuevo saldo: $${newBalance}
    `,
    icon: "success",
    button: {
      text: "Continuar",
      className: "bg-green-500 hover:bg-green-600"
    }
  });
};
